import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  FaUser, FaVideo, FaBell, FaFileAlt, FaGavel,
  FaCheckCircle, FaHourglassHalf, FaDownload, FaHistory, FaChevronDown, FaChevronUp
} from "react-icons/fa";

const MEETING_LABEL = { 1: "1st Hearing", 2: "2nd Hearing", 3: "3rd Hearing" };

const STATUS_META = {
  pending:          { label: "Pending",               color: "#6c757d" },
  assigned:         { label: "Hearing Assigned",       color: "#0d6efd" },
  notice_generated: { label: "Notice Generated",       color: "#fd7e14" },
  award_uploaded:   { label: "Award Issued (Partial)", color: "#17a2b8" },
  done:             { label: "Done",                   color: "#198754" },
};

const SectionCard = ({ icon, title, accentColor, children }) => (
  <div style={{ background: "#fff", borderRadius: "10px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 18px", borderBottom: "1px solid #e9ecef" }}>
      <span style={{ color: accentColor, fontSize: "1rem", lineHeight: 1 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#333", letterSpacing: "0.2px" }}>{title}</span>
    </div>
    <div style={{ padding: "16px 18px" }}>{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.6px", color: "#999", marginBottom: "3px" }}>{label}</div>
    <div style={{ fontWeight: 600, color: "#222" }}>{value || "—"}</div>
  </div>
);

const DocRow = ({ ready, readyText, pendingText, onDownload, downloadLabel }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {ready
        ? <FaCheckCircle style={{ color: "#198754", fontSize: "1rem" }} />
        : <FaHourglassHalf style={{ color: "#adb5bd", fontSize: "0.9rem" }} />
      }
      <span style={{ fontWeight: 600, color: ready ? "#198754" : "#999" }}>
        {ready ? readyText : pendingText}
      </span>
    </div>
    {ready && onDownload && (
      <button
        className="custBtn"
        onClick={onDownload}
        style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.78rem", padding: "4px 12px" }}
      >
        <FaDownload style={{ fontSize: "0.72rem" }} /> {downloadLabel}
      </button>
    )}
  </div>
);

const ORDINAL = ["", "1st", "2nd", "3rd", "4th", "5th", "6th"];

const ContestViewModal = ({ show, onHide, viewCase, onDownloadNotice, onDownloadRoznama, onDownloadAward, onDownloadCertifiedAward }) => {
  const [expandedRound, setExpandedRound] = useState(null);
  if (!viewCase) return null;

  const awardUploaded     = viewCase.certifiedOrderUploaded;
  const certAwardUploaded = viewCase.certifiedAwardUploaded;
  const resetHistory      = viewCase.resetHistory ?? [];
  const hearingCycle      = viewCase.hearingCycle ?? 1;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="customModal">
        <Modal.Title className="mx-auto" style={{ fontSize: "1rem" }}>
          {viewCase.referenceNo} &nbsp;·&nbsp; {viewCase.custName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#f4f6f9", padding: "20px", fontSize: "0.84rem" }}>
        <div className="d-flex flex-column gap-3">

          {/* a. Case Details */}
          <SectionCard icon={<FaUser />} title="Case Details" accentColor="#0d6efd">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "16px" }}>
              <Field label="Reference No" value={viewCase.referenceNo} />
              <Field label="Customer Name" value={viewCase.custName} />
              <Field label="Account No" value={viewCase.accNo} />
              <Field label="Lot No" value={viewCase.lotNo} />
              <Field label="Contested At" value={MEETING_LABEL[viewCase.contestedMeetingNo] ?? "—"} />
              <Field label="Contested Date" value={viewCase.contestedDate} />
              <Field label="Hearing Round" value={`${ORDINAL[hearingCycle] || `${hearingCycle}th`} round${hearingCycle > 1 ? ` (${hearingCycle - 1} re-hearing${hearingCycle > 2 ? "s" : ""})` : ""}`} />
              <Field
                label="Hearing Time"
                value={viewCase.hearingTimeFrom && viewCase.hearingTimeTo
                  ? `${viewCase.hearingTimeFrom} – ${viewCase.hearingTimeTo}` : null}
              />
              <div>
                <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.6px", color: "#999", marginBottom: "3px" }}>Status</div>
                <span style={{ background: STATUS_META[viewCase.contestStatus]?.color, color: "#fff", padding: "3px 12px", borderRadius: "20px", fontSize: "0.78rem", fontWeight: 600 }}>
                  {STATUS_META[viewCase.contestStatus]?.label}
                </span>
              </div>
            </div>
          </SectionCard>

          {/* b. Zoom Meeting */}
          <SectionCard icon={<FaVideo />} title="Zoom Meeting" accentColor="#6610f2">
            {viewCase.contestNextHearingDate ? (
              <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.6px", color: "#999", marginBottom: "3px" }}>Date</div>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>{viewCase.contestNextHearingDate}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.6px", color: "#999", marginBottom: "3px" }}>Time</div>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>
                    {viewCase.contestTimeFrom || "—"} – {viewCase.contestTimeTo || "—"}
                  </div>
                </div>
              </div>
            ) : (
              <span style={{ color: "#adb5bd", fontStyle: "italic" }}>Not assigned yet</span>
            )}
          </SectionCard>

          {/* c. Hearing Notices */}
          <SectionCard icon={<FaBell />} title="Hearing Notices" accentColor="#fd7e14">
            <DocRow
              ready={viewCase.hasNotice}
              readyText="Notice Generated"
              pendingText="Not generated yet"
              onDownload={() => onDownloadNotice(viewCase.caseId)}
              downloadLabel="Download Notice"
            />
          </SectionCard>

          {/* d. Roznama */}
          <SectionCard icon={<FaFileAlt />} title="Roznama" accentColor="#20c997">
            <DocRow
              ready={viewCase.hasRoznama}
              readyText="Roznama Generated"
              pendingText="Not generated yet"
              onDownload={() => onDownloadRoznama(viewCase.caseId)}
              downloadLabel="Download Roznama"
            />
          </SectionCard>

          {/* e. Hearing History — shown only if this case has been reset at least once */}
          {resetHistory.length > 0 && (
            <SectionCard icon={<FaHistory />} title={`Hearing History (${resetHistory.length} previous round${resetHistory.length > 1 ? "s" : ""})`} accentColor="#6c757d">
              <div className="d-flex flex-column gap-2">
                {[...resetHistory].reverse().map((entry, idx) => {
                  const isOpen = expandedRound === idx;
                  const label = `${ORDINAL[entry.cycle] || `${entry.cycle}th`} Hearing Round`;
                  const resetDate = entry.resetAt ? new Date(entry.resetAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
                  return (
                    <div key={idx} style={{ border: "1px solid #e9ecef", borderRadius: "8px", overflow: "hidden" }}>
                      <button
                        onClick={() => setExpandedRound(isOpen ? null : idx)}
                        style={{
                          width: "100%", background: isOpen ? "#f8f9fa" : "#fff",
                          border: "none", padding: "10px 14px",
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          cursor: "pointer", fontSize: "0.83rem",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#555" }}>
                          {label}
                          <span style={{ marginLeft: "10px", fontWeight: 400, color: "#999", fontSize: "0.78rem" }}>
                            moved back on {resetDate} · was <em>{entry.resetFromStatus?.replace(/_/g, " ")}</em>
                          </span>
                        </span>
                        {isOpen ? <FaChevronUp style={{ color: "#aaa" }} /> : <FaChevronDown style={{ color: "#aaa" }} />}
                      </button>

                      {isOpen && (
                        <div style={{ padding: "12px 16px", background: "#fafafa", borderTop: "1px solid #e9ecef" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", fontSize: "0.8rem" }}>
                            {entry.nextHearingDate && (
                              <div>
                                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "#aaa", marginBottom: "2px" }}>Hearing Date</div>
                                <div style={{ fontWeight: 600, color: "#444" }}>{new Date(entry.nextHearingDate).toLocaleDateString("en-IN")}</div>
                              </div>
                            )}
                            {(entry.hearingTimeFrom || entry.hearingTimeTo) && (
                              <div>
                                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "#aaa", marginBottom: "2px" }}>Time</div>
                                <div style={{ fontWeight: 600, color: "#444" }}>{entry.hearingTimeFrom} – {entry.hearingTimeTo}</div>
                              </div>
                            )}
                            {(entry.videoTimeFrom || entry.videoTimeTo) && (
                              <div>
                                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "#aaa", marginBottom: "2px" }}>Video Time</div>
                                <div style={{ fontWeight: 600, color: "#444" }}>{entry.videoTimeFrom} – {entry.videoTimeTo}</div>
                              </div>
                            )}
                            {entry.videoComment && (
                              <div style={{ gridColumn: "1 / -1" }}>
                                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "#aaa", marginBottom: "2px" }}>Comment</div>
                                <div style={{ color: "#444" }}>{entry.videoComment}</div>
                              </div>
                            )}
                          </div>
                          <div className="d-flex gap-2 flex-wrap mt-2">
                            {entry.noticeFilePath && <span style={{ fontSize: "0.75rem", background: "#e9ecef", borderRadius: "4px", padding: "2px 8px", color: "#555" }}>✓ Notice</span>}
                            {entry.roznamaFilePath && <span style={{ fontSize: "0.75rem", background: "#e9ecef", borderRadius: "4px", padding: "2px 8px", color: "#555" }}>✓ Roznama</span>}
                            {entry.videoFilePath && <span style={{ fontSize: "0.75rem", background: "#e9ecef", borderRadius: "4px", padding: "2px 8px", color: "#555" }}>✓ Video</span>}
                            {entry.certifiedOrderFilePath && <span style={{ fontSize: "0.75rem", background: "#e9ecef", borderRadius: "4px", padding: "2px 8px", color: "#555" }}>✓ Award (1st doc)</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {/* f. Award Issued — only shown as ready when certified award (2nd doc) is uploaded */}
          <SectionCard icon={<FaGavel />} title="Award Issued" accentColor={certAwardUploaded ? "#198754" : "#adb5bd"}>
            <div className="d-flex flex-column gap-2">
              <DocRow
                ready={awardUploaded}
                readyText="Award uploaded"
                pendingText="Award not uploaded yet"
                onDownload={awardUploaded ? () => onDownloadAward(viewCase.caseId) : null}
                downloadLabel="Download Award"
              />
              <DocRow
                ready={certAwardUploaded}
                readyText="Certified Award uploaded"
                pendingText="Certified Award not uploaded yet"
                onDownload={certAwardUploaded ? () => onDownloadCertifiedAward(viewCase.caseId) : null}
                downloadLabel="Download Certified Award"
              />
            </div>
          </SectionCard>

        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ContestViewModal;
